import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useTheme } from '../ThemeContext';

const F = "'Fira Code', monospace";

const Expenses = ({ data, onUpdate, user }) => {
  const { T } = useTheme();
  const [itemName, setItemName] = useState('');
  const [amount, setAmount] = useState('');
  const [source, setSource] = useState('salary');
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const listRef = useRef(null);

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 2500); };

  const handleAddExpense = async () => {
    const amt = parseFloat(amount);
    if (!itemName.trim()) return showFlash('✗ ENTER ITEM NAME');
    if (!amt || amt <= 0) return showFlash('✗ ENTER VALID AMOUNT');
    if (source === 'salary' && amt > (data.salaryBalance || 0)) return showFlash('✗ INSUFFICIENT SALARY BALANCE');
    if (source === 'stocks' && amt > (data.stocksTotalProfit || 0)) return showFlash('✗ INSUFFICIENT STOCKS PROFIT');
    setSaving(true);
    const entry = { item: itemName.trim(), amount: amt, source, date: new Date().toISOString() };
    const expenses = [...(data.expenses || []), entry];
    const totalExpenses = expenses.reduce((s, e) => s + e.amount, 0);
    let updates = { expenses, totalExpenses };
    if (source === 'salary') {
      const nb = (data.salaryBalance || 0) - amt; updates.salaryBalance = nb;
      try { await setDoc(doc(db, 'users', user.uid, 'data', 'salary'), { currentBalance: nb }, { merge: true }); } catch(e) {}
    } else {
      const np = (data.stocksTotalProfit || 0) - amt; updates.stocksTotalProfit = np;
      try { await setDoc(doc(db, 'users', user.uid, 'data', 'stocks'), { totalProfit: np }, { merge: true }); } catch(e) {}
    }
    try {
      await setDoc(doc(db, 'users', user.uid, 'data', 'expenses'), { items: expenses, totalExpenses }, { merge: true });
      onUpdate(updates); setItemName(''); setAmount('');
      showFlash('✓ EXPENSE LOGGED & DEDUCTED');
    } catch (e) { showFlash('✗ FIREBASE WRITE FAILED'); }
    setSaving(false);
  };

  const handleDeleteExpense = async (idx) => {
    const realIdx = (data.expenses || []).length - 1 - idx;
    const expense = (data.expenses || [])[realIdx];
    if (!expense) return;
    const expenses = [...(data.expenses || [])]; expenses.splice(realIdx, 1);
    const totalExpenses = expenses.reduce((s, e) => s + e.amount, 0);
    let updates = { expenses, totalExpenses };
    if (expense.source === 'salary') {
      const nb = (data.salaryBalance || 0) + expense.amount; updates.salaryBalance = nb;
      try { await setDoc(doc(db, 'users', user.uid, 'data', 'salary'), { currentBalance: nb }, { merge: true }); } catch(e) {}
    } else {
      const np = (data.stocksTotalProfit || 0) + expense.amount; updates.stocksTotalProfit = np;
      try { await setDoc(doc(db, 'users', user.uid, 'data', 'stocks'), { totalProfit: np }, { merge: true }); } catch(e) {}
    }
    try {
      await setDoc(doc(db, 'users', user.uid, 'data', 'expenses'), { items: expenses, totalExpenses }, { merge: true });
      onUpdate(updates); showFlash('✓ EXPENSE DELETED & REFUNDED');
    } catch (e) { showFlash('✗ DELETE FAILED'); }
    setDeleteConfirm(null);
  };

  const totalExp   = (data.expenses || []).reduce((s, e) => s + e.amount, 0);
  const salaryExp  = (data.expenses || []).filter(e => e.source === 'salary').reduce((s, e) => s + e.amount, 0);
  const stocksExp  = (data.expenses || []).filter(e => e.source === 'stocks').reduce((s, e) => s + e.amount, 0);

  const dailyExp = (() => {
    const map = {};
    (data.expenses || []).forEach(e => { const day = e.date?.slice(0, 10); if (day) map[day] = (map[day] || 0) + e.amount; });
    return Object.entries(map).slice(-12).map(([date, total]) => ({ date, total }));
  })();
  const maxDailyExp = Math.max(...dailyExp.map(d => d.total), 1);

  const topItems = (() => {
    const map = {};
    (data.expenses || []).forEach(e => { map[e.item.toUpperCase()] = (map[e.item.toUpperCase()] || 0) + e.amount; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 5);
  })();
  const topMax = topItems.length > 0 ? topItems[0][1] : 1;

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  }, [data.expenses]);

  const cardS = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '22px 22px', marginBottom: 14, boxShadow: T.isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.06)', transition: 'background .3s,border .3s' };
  const hdrS  = { fontSize: 10, letterSpacing: '0.25em', color: T.text, fontFamily: F, marginBottom: 18, fontWeight: 700, paddingBottom: 10, borderBottom: `1px solid ${T.border}` };

  return (
    <div style={{ maxWidth: 780, margin: '0 auto' }}>
      {flash && <Flash msg={flash} T={T} />}

      {/* Overview Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <SC label="TOTAL EXPENDITURE" value={`₹${totalExp.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}  color={T.rose}    T={T} />
        <SC label="FROM SALARY"       value={`₹${salaryExp.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`} color={T.sky}     T={T} />
        <SC label="FROM STOCKS"       value={`₹${stocksExp.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`} color={T.cyan}    T={T} />
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 12, marginBottom: 16 }}>
        {dailyExp.length > 0 && (
          <div style={cardS}>
            <div style={hdrS}>📊 DAILY SPENDING</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 90, padding: '0 4px' }}>
              {dailyExp.map((d, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }} title={`${d.date}: ₹${d.total}`}>
                  <span style={{ fontSize: 7, color: T.dim }}>₹{(d.total/1000).toFixed(0)}k</span>
                  <div style={{ width: '75%', maxWidth: 24, minHeight: 4, height: `${(d.total / maxDailyExp) * 70}px`, background: `linear-gradient(180deg, ${T.rose}, ${T.rose}33)`, borderRadius: '3px 3px 0 0', transition: 'height 0.5s ease' }} />
                  <span style={{ fontSize: 6, color: T.dim }}>{d.date.slice(8)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {topItems.length > 0 && (
          <div style={cardS}>
            <div style={hdrS}>🔝 TOP SPENDING</div>
            {topItems.map(([item, val], i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, marginBottom: 4 }}>
                  <span style={{ color: T.text, letterSpacing: '0.1em' }}>{item}</span>
                  <span style={{ color: T.rose }}>₹{val.toLocaleString('en-IN')}</span>
                </div>
                <div style={{ width: '100%', height: 4, background: T.muted, borderRadius: 2, opacity: T.isDark ? 0.3 : 0.2 }}>
                  <div style={{ height: 4, borderRadius: 2, width: `${(val / topMax) * 100}%`, background: `linear-gradient(90deg, ${T.rose}, ${T.violet})`, transition: 'width 0.5s ease' }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Expense Form */}
      <div style={cardS}>
        <div style={hdrS}>💸 LOG EXPENSE</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 14, marginBottom: 14 }}>
          <IB label="ITEM NAME" type="text" value={itemName} onChange={setItemName} onEnter={handleAddExpense} placeholder="Groceries, Petrol..." T={T} />
          <IB label="AMOUNT (₹)" type="number" value={amount} onChange={setAmount} onEnter={handleAddExpense} T={T} />
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={{ fontSize: 8, letterSpacing: '0.35em', color: T.dim, fontFamily: F, marginBottom: 6, display: 'block' }}>&gt; DEDUCT FROM</label>
          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            {[
              { key: 'salary', label: 'SALARY',        bal: data.salaryBalance || 0,      color: T.sky },
              { key: 'stocks', label: 'STOCKS PROFIT', bal: data.stocksTotalProfit || 0,  color: T.cyan },
            ].map(opt => (
              <div key={opt.key} onClick={() => setSource(opt.key)} style={{ flex: 1, padding: '12px 14px', borderRadius: 8, cursor: 'pointer', background: source === opt.key ? `${opt.color}10` : 'transparent', border: `1px solid ${source === opt.key ? opt.color + '66' : T.border}`, transition: 'all 0.25s' }}>
                <div style={{ fontSize: 8, letterSpacing: '0.25em', color: source === opt.key ? opt.color : T.dim, fontFamily: F, marginBottom: 4 }}>{opt.label}</div>
                <div style={{ fontSize: 14, color: source === opt.key ? opt.color : T.dim, fontFamily: F, fontWeight: 700 }}>₹{opt.bal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
              </div>
            ))}
          </div>
        </div>
        <BtnX onClick={handleAddExpense} disabled={saving} label={saving ? 'PROCESSING...' : 'LOG EXPENSE'} color={T.rose} />
      </div>

      {/* Expense List */}
      <div style={cardS}>
        <div style={{ ...hdrS, display: 'flex', justifyContent: 'space-between' }}>
          <span>📋 EXPENSE LOG</span>
          <span style={{ fontSize: 8, color: T.dim, fontWeight: 400 }}>{(data.expenses || []).length} ITEMS</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.8fr 0.8fr 0.7fr 0.3fr', gap: 4, padding: '8px 10px', fontSize: 7, letterSpacing: '0.3em', color: T.dim, borderBottom: `1px solid ${T.border}` }}>
          <span>ITEM</span><span>AMOUNT</span><span>SOURCE</span><span>DATE</span><span></span>
        </div>
        <div ref={listRef} style={{ maxHeight: 280, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: `${T.scrollC} transparent` }}>
          {(data.expenses || []).length === 0 && (
            <div style={{ textAlign: 'center', color: T.dim, fontSize: 10, padding: 30, letterSpacing: '0.2em' }}>NO EXPENSES RECORDED</div>
          )}
          {[...(data.expenses || [])].reverse().map((exp, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.8fr 0.8fr 0.7fr 0.3fr', gap: 4, padding: '11px 10px', borderBottom: `1px solid ${T.border}`, fontSize: 10, alignItems: 'center' }}>
              <span style={{ color: T.text, textTransform: 'uppercase', fontSize: 10 }}>{exp.item}</span>
              <span style={{ color: T.rose, fontWeight: 700 }}>₹{exp.amount?.toLocaleString('en-IN')}</span>
              <span style={{ fontSize: 8, padding: '2px 6px', borderRadius: 3, textAlign: 'center', background: exp.source === 'salary' ? `${T.sky}18` : `${T.cyan}18`, color: exp.source === 'salary' ? T.sky : T.cyan }}>
                {exp.source === 'salary' ? 'SAL' : 'STK'}
              </span>
              <span style={{ color: T.dim, fontSize: 9 }}>{new Date(exp.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
              <span>
                {deleteConfirm === i ? (
                  <span onClick={() => handleDeleteExpense(i)} style={{ color: T.rose, cursor: 'pointer', fontSize: 8, fontWeight: 900 }}>OK?</span>
                ) : (
                  <span onClick={() => setDeleteConfirm(i)} style={{ color: T.muted, cursor: 'pointer', fontSize: 11, transition: 'color 0.2s' }}
                    onMouseOver={e => e.currentTarget.style.color = T.rose} onMouseOut={e => e.currentTarget.style.color = T.muted}>✕</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* Shared */
const Flash = ({ msg, T }) => (
  <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 999, background: msg.startsWith('✓') ? `${T.emerald}18` : `${T.rose}18`, border: `1px solid ${msg.startsWith('✓') ? T.emerald : T.rose}`, color: msg.startsWith('✓') ? T.emerald : T.rose, padding: '10px 28px', borderRadius: 6, fontSize: 11, letterSpacing: '0.2em', fontFamily: F, animation: 'flashIn 0.3s ease' }}>
  {msg}<style>{`@keyframes flashIn { from{opacity:0;transform:translateX(-50%) translateY(-8px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }`}</style>
  </div>
);

const SC = ({ label, value, color, T }) => (
  <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '18px 16px', textAlign: 'center', position: 'relative', overflow: 'hidden', boxShadow: T.isDark ? 'none' : '0 2px 10px rgba(0,0,0,0.05)', transition: 'background .3s' }}>
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: color, opacity: 0.5 }} />
    <div style={{ fontSize: 7, letterSpacing: '0.35em', color: T.dim, fontFamily: F, marginBottom: 10 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 900, color, fontFamily: F }}>{value}</div>
  </div>
);

const IB = ({ label, type, value, onChange, onEnter, placeholder, T }) => (
  <div>
    <label style={{ fontSize: 8, letterSpacing: '0.35em', color: T.dim, fontFamily: F, marginBottom: 6, display: 'block' }}>&gt; {label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} onKeyDown={e => e.key === 'Enter' && onEnter?.()} placeholder={placeholder || '0.00'}
      style={{ width: '100%', background: T.inputBg, border: 'none', borderBottom: `1.5px solid ${T.inputBdr}`, padding: '10px 8px', color: T.text, fontSize: 14, letterSpacing: '0.1em', outline: 'none', fontFamily: F, caretColor: T.inputFocus }}
      onFocus={e => e.target.style.borderBottomColor = T.inputFocus} onBlur={e => e.target.style.borderBottomColor = T.inputBdr} />
  </div>
);

const BtnX = ({ onClick, disabled, label, color }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ width: '100%', background: 'transparent', border: `1px solid ${color}44`, color, padding: '12px 0', fontSize: 10, letterSpacing: '0.5em', fontWeight: 700, cursor: 'pointer', fontFamily: F, borderRadius: 6, transition: 'all 0.25s' }}
    onMouseOver={e => { e.currentTarget.style.background = color + '15'; e.currentTarget.style.borderColor = color; }}
    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = color + '44'; }}
  >{label}</button>
);

export default Expenses;
